import { Component, OnInit, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { State, process, SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { BaseService } from '../../ve.service';
import { CgpConfirmationDialogService } from '../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpSnackbarService } from '../../../../shared/cgp-snackbar/cgp-snackbar.service';
import { AdministrationService } from '../administration.service';
import { CgpAlertDialogService } from '../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { AuthenticationService } from '../../../../auth.service';

const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

@Component({
  selector: 'app-base-grid',
  templateUrl: './base-grid.component.html',
})

export class BaseGridComponent implements OnInit {
  public readonly adminService: AdministrationService;
  public authService: AuthenticationService;
  private readonly cgpAlertDialogService: CgpAlertDialogService;
  private editedRowIndex: number;
  public editForm: UntypedFormGroup;

  appCardTitle: string;
  gridData: Observable<GridDataResult>;
  data: any[] = [];
  gridState: State;
  sort: SortDescriptor[] = [];
  columns: any[] = [];
  selectedDropdownValue: string;
  filter: CompositeFilterDescriptor;
  formBuilder: UntypedFormBuilder;
  router: Router;
  dropdownList: any[];
  path: any;

  constructor(
    protected cgpConfirmationDialogService: CgpConfirmationDialogService,
    protected cgpSnackbarService: CgpSnackbarService,
    protected service: BaseService,
    protected injector: Injector,
  ) {
    this.adminService = injector.get(AdministrationService);
    this.authService = injector.get(AuthenticationService);
    this.cgpAlertDialogService = injector.get(CgpAlertDialogService);
    this.formBuilder = this.injector.get(UntypedFormBuilder);
    this.router = this.injector.get(Router);
    this.selectedDropdownValue = this.adminService.getCurrentPageValue();
    this.sort = [{ field: 'Name', dir: 'asc' }];
    this.gridState = {
      skip: 0,
      take: 10,
      sort: this.sort,
      filter: this.filter
    } as State;
  }

  ngOnInit() {
    this.dropdownList = this.adminService.getDropdownList();
    this.refresh();
  }

  /**
   * Refresh grid
   */
  protected refresh() {
    this.service.read(this.path)
      .then(response => {
        // Alphabetically sort grid
        if (response.find(obj => obj.GateOrder != null)) {
          response.sort((a, b) => a.GateOrder - b.GateOrder);
        } else if (response.find(obj => obj.DisplayOrder != null)) {
          response.sort((a, b) => a.DisplayOrder - b.DisplayOrder);
        } else if (response.find(obj => obj.Description != null)) {
          response.sort((a, b) => a.Description.localeCompare(b.Description));
        } else if (response.find(obj => obj.ProgramCode != null)) {
          response.sort((a, b) => a.ProgramCode.localeCompare(b.ProgramCode));
        }

        if (response.find(obj => obj.HolidayDate != null)) {
          for (const element of response) {
            const temp = new Date(element.HolidayDate);
            element.HolidayDate = temp;
          }
        }

        for (const column of this.columns) {
          if (column.refreshSecondaryData) {
            column.refreshSecondaryData(this);
          }
        }

        this.gridData = of(process(response, this.gridState));
        this.data = response;
      });
  }

  /**
   *
   * @param state
   */
  onStateChange(state: State) {
    this.gridState = state;
    this.refresh();
  }

  /**
   *
   * @param sender
   */
  addHandler = ({ sender }) => {
    this.closeEditor(sender);
    Object.keys(this.editForm.controls).forEach(key => {
      if (key.toLowerCase() === 'isactive' ||
          key.toLowerCase() === 'issecure' ||
          key.toLowerCase() === 'isrequired' ||
          key.toLowerCase() === 'includeintemplate') {
        this.editForm.get(key).setValue(true);
      } else {
        this.editForm.get(key).setValue(null);
      }
      this.editForm.get(key).enable();
    });
    sender.addRow(this.editForm);
  }

  /**
   *
   * @param sender
   * @param rowIndex
   * @param formGroup
   * @param isNew
   *
   */
  saveHandler({ sender, rowIndex, formGroup, isNew }) {
    Object.keys(formGroup.controls).forEach(key => {
      this.editForm.get(key).enable();
    });

    const data: any = formGroup.value;
    const path = isNew ? '' : `/${data.Id}`;
    const action = isNew ? 'added' : 'edited';
    data.ModuleName = this.adminService.userModule;

    this.service.save(data, isNew, path, false)
      .then(() => {
        this.refresh();
        const msg = data.Description ? `${data.Description} was ${action}.` : `Record was ${action}.`;
        this.showSnackbar(msg);
      })
      .catch(error => {
        this.cgpAlertDialogService.showAlertDialog({
          title: 'Error',
          message: error.error,
          alert: 'danger',
          closeLabel: 'OK',
        });
        const errMsg = data.Description ? `${data.Description} was not saved.` : `Record was not ${action}.`;
        this.showSnackbar(errMsg);
      });

    sender.closeRow(rowIndex);
  }

  /**
   * @param sender
   * @param rowIndex
   */
  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  /**
   * @param sender
   * @param rowIndex
   */
  closeEditor(grid, rowIndex = this.editedRowIndex) {

    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    if (this.editForm.get('SMT1Id')) {
      this.editForm.get('SMT1Id').enable();
    }
    if (this.editForm.get('SMT2Id')) {
      this.editForm.get('SMT2Id').enable();
    }
    if (this.editForm.get('SMT3Id')) {
      this.editForm.get('SMT3Id').enable();
    }
  }

  /**
   * @param sender
   * @param rowIndex
   * @param dataItem
   */
  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    Object.keys(this.editForm.controls).forEach(key => {
      if (key.toLowerCase() === 'programcode') {
        this.editForm.get(key).disable();
      }
      if (key.toLowerCase() === 'workorderstatusdescription') {
        this.editForm.get(key).disable();
      }
      this.editForm.get(key).setValue(dataItem[key]);
    });

    for (const column of this.columns) {
      if (column.resetFilter && this.editForm.get('SMT1Id') && column.title === 'SMT 2') {
        column.resetFilter(this.editForm.get('SMT1Id').value, this);
      }
      if (column.resetFilter && this.editForm.get('SMT2Id') && column.title === 'SMT 3') {
        column.resetFilter(this.editForm.get('SMT2Id').value, this);
      }
    }

    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.editForm);
  }

  removeHandler({ dataItem }) {
    const date = dataItem.HolidayDate.toLocaleDateString('en-US', options);
    this.cgpConfirmationDialogService.showConfirmationDialog({
      title: `Delete "${date}"?`,
      message: `This will delete the holiday scheduled on "${date}"`,
      yesLabel: 'Yes',
      noLabel: 'No',
      alert: 'danger',
      disableClose: 'true',
      onYesClick: () => {
        this.cgpConfirmationDialogService.closeConfirmationDialog();
        this.service.remove(`/${dataItem.Id}`)
          .then(() => {
            this.refresh();
            this.showSnackbar(`${date} was deleted.`);
          });
      },
      onNoClick: () => { this.cgpConfirmationDialogService.closeConfirmationDialog(); }
    });
  }

  showSnackbar = (message: string) => {
    this.cgpSnackbarService.showSnackbar({
      message,
      duration: 5000,
    });
  }

  getExcelExportFileName(): string {
    const date = new Date();
    const fileType = '.xlsx';
    return [
      this.appCardTitle ? this.appCardTitle.replace(/\s/g, '') : '',
      (date.getMonth() + 1),
      date.getDate(),
      date.getFullYear()
    ].join('_') + fileType;
  }

  back = () => {
    this.router.navigate(['VE']);
  }

  formatData(): any {
    const dataCopy = JSON.parse(JSON.stringify(this.data));

    if (dataCopy[0].hasOwnProperty('TotalVehiclesNumbers')) {
      dataCopy.forEach(dataItem => {
        const tempArray = dataItem.TotalVehiclesNumbers;
        dataItem.TotalVehiclesNumbers = tempArray.join(', ');
      });
    }

    if (dataCopy[0].hasOwnProperty('TrackingVehiclesNumbers')) {
      dataCopy.forEach(dataItem => {
        const tempArray = dataItem.TrackingVehiclesNumbers;
        dataItem.TrackingVehiclesNumbers = tempArray.join(', ');
      });
    }

    if (dataCopy[0].hasOwnProperty('ZoneNames')) {
      dataCopy.forEach(dataItem => {
        const tempArray = dataItem.ZoneNames;
        dataItem.ZoneNames = tempArray.join(', ');
      });
    }

    if (dataCopy[0].hasOwnProperty('ContactList')) {
      dataCopy.forEach(dataItem => {
        const tempArray = dataItem.ContactList;
        dataItem.ContactList = tempArray.join(', ');
      });
    }

    return dataCopy;
  }

  public allData = (): ExcelExportData => {
    const data = this.formatData();
    const state = { group: this.gridState.group, sort: this.gridState.sort };
    const group = this.gridState.group;

    return {
      data: process(data, state).data,
      group
    };
  }

  /**
   *
   * @param context
   * Used to set alternating row colors in grid
   */
  public rowCallback(context: RowClassArgs) {
    const isEven = context.index % 2 === 0;
    return {
      even: isEven,
      odd: !isEven
    };
  }

  public isColumnDisabled = (column: any, isNew: any): boolean => {
    if ((column.isEditingDisabled && !isNew) || (column.isDisabled)) {
      return true;
    }
    return false;
  }

  onValueChange(event, dataItem) {
    //this is kept empty on purpose
  }

  canEdit() {
    if (this.appCardTitle === 'Holiday Schedules') { return false; }
    return true;
  }
}

